import ColumnDatePicker from 'components/ColumnDatePicker';
import CheckboxGroup, {
  CheckboxGroupOption
} from 'lib/components/Checkbox/CheckboxGroup';
import { Label } from 'lib/components/Label';
import moment from 'moment';
import { ColumnSelect } from 'lib/components/ColumnSelect';
import {
  ALL_INVOICE_CREATION_DATES,
  ALL_INVOICES_STATUS,
  CreationDatesOptionValue,
  CUSTOM_INVOICE_CREATION_DATES,
  INVOICE_CREATION_DATE_OPTIONS,
  LAST_MONTH_INVOICE_CREATION_DATES,
  THIS_MONTH_INVOICE_CREATION_DATES
} from './invoiceTableSettingsUtils';
import {
  PublisherInvoiceTableFilter,
  PublisherInvoiceTableStatus,
  PUBLISHER_INVOICE_STATUS_OPTIONS
} from './publisherInvoiceTableSettingsUtils';

type PublisherInvoicesFilterDialogProps = {
  setUpdatedFilter: (filter: PublisherInvoiceTableFilter) => void;
  updatedFilter: PublisherInvoiceTableFilter;
};

export default function PublisherInvoicesFilterDialog({
  setUpdatedFilter,
  updatedFilter
}: PublisherInvoicesFilterDialogProps) {
  const isCustomDateRange = ![
    ALL_INVOICE_CREATION_DATES,
    THIS_MONTH_INVOICE_CREATION_DATES,
    LAST_MONTH_INVOICE_CREATION_DATES,
    {}
  ].includes(updatedFilter.dateRange);

  // parse the date range into a more easily typed format
  const currentDateRange = isCustomDateRange ? updatedFilter.dateRange : null;

  return (
    <>
      <p className="uppercase text-column-gray-300 text-xs font-bold">
        Filter by
      </p>

      {/* Control what subset of invoices by status we're looking at */}
      <CheckboxGroup<PublisherInvoiceTableStatus>
        id="publisher-invoice-status-filter"
        labelText="Status"
        stacked
        options={PUBLISHER_INVOICE_STATUS_OPTIONS.map(
          ({
            label,
            value
          }): CheckboxGroupOption<PublisherInvoiceTableStatus> => ({
            id: value,
            labelText: label,
            checked: updatedFilter.statuses.includes(value)
          })
        )}
        onChange={(newOptions, changedOption) => {
          let newStatuses: PublisherInvoiceTableStatus[];
          if (
            changedOption.id === ALL_INVOICES_STATUS &&
            changedOption.checked
          ) {
            newStatuses = [ALL_INVOICES_STATUS];
          } else {
            const specificSelected = newOptions
              .filter(opt => opt.id !== ALL_INVOICES_STATUS && opt.checked)
              .map(opt => opt.id);
            newStatuses =
              specificSelected.length > 0
                ? specificSelected
                : [ALL_INVOICES_STATUS];
          }
          setUpdatedFilter({
            ...updatedFilter,
            statuses: newStatuses
          });
        }}
      />

      {/* Control the invoice creation dates visible in the table */}
      <ColumnSelect
        id="publisher-invoice-date-created"
        labelText="Date Created"
        size="sm"
        options={INVOICE_CREATION_DATE_OPTIONS}
        value={
          isCustomDateRange
            ? CUSTOM_INVOICE_CREATION_DATES
            : updatedFilter.dateRange.toString()
        }
        onChange={newValue => {
          if (newValue === CUSTOM_INVOICE_CREATION_DATES) {
            setUpdatedFilter({
              ...updatedFilter,
              dateRange: {
                start: moment().subtract(1, 'month').toDate(),
                end: moment().toDate()
              }
            });
          } else {
            setUpdatedFilter({
              ...updatedFilter,
              dateRange: newValue as CreationDatesOptionValue
            });
          }
        }}
      />

      {/* Filter on a custom date range */}
      {currentDateRange && typeof currentDateRange !== 'string' && (
        <div>
          <Label id="publisher-invoice-date-filter-label">
            Choose a date range
          </Label>
          <div className="flex flex-row gap-2">
            <ColumnDatePicker
              className="w-1/2"
              format="MMM dd"
              momentFormat="MMM DD"
              placeholderText="Start date"
              value={currentDateRange?.start}
              onChange={newDate =>
                newDate &&
                setUpdatedFilter({
                  ...updatedFilter,
                  dateRange: {
                    ...currentDateRange,
                    start: newDate
                  }
                })
              }
              shouldDisableDate={date =>
                !date || moment(date).isAfter(currentDateRange.end)
              }
            />
            <ColumnDatePicker
              className="w-1/2"
              format="MMM dd"
              momentFormat="MMM DD"
              placeholderText="End date"
              value={currentDateRange.end}
              onChange={newDate =>
                newDate &&
                setUpdatedFilter({
                  ...updatedFilter,
                  dateRange: {
                    ...currentDateRange,
                    end: newDate
                  }
                })
              }
              shouldDisableDate={date => !date || moment().isBefore(date)}
            />
          </div>
        </div>
      )}
    </>
  );
}
